import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { CostCenter, InsertCostCenter, UpdateCostCenter } from "./types";

const useCostCenterApi = () => {
  const baseurl = "/cost_centers";

  const api = useApi();

  const searchCostCenters = useCallback(
    async (query: PaginationQuery, signal?: AbortSignal) => {
      const url = buildUrl(baseurl, query);
      const data = await api.get<Paginated<CostCenter>>({ url, signal });
      return data.items;
    },
    [api]
  );

  const useCostCenters = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl, query);
      const result = useQuery<Paginated<CostCenter>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<CostCenter>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api]
  );

  const createCostCenter = useCallback(
    async (costCenter: InsertCostCenter) => {
      const data = await api.post<InsertCostCenter, CostCenter>(baseurl, costCenter);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const updateCostCenter = useCallback(
    async (costCenter: UpdateCostCenter, costCentersCode: string) => {
      const data = await api.put<UpdateCostCenter>(`${baseurl}/${costCentersCode}`, costCenter);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const deleteCostCenter = useCallback(
    async (costCentersCode: string) => {
      const data = await api.del(`${baseurl}/${costCentersCode}`);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  return {
    searchCostCenters,
    useCostCenters,
    createCostCenter,
    updateCostCenter,
    deleteCostCenter,
  };
};

export default useCostCenterApi;
